import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { staffAPI, excelGeneratorAPI } from '../services/api';
import { playSuccessSound, playErrorSound, playWarningSound } from '../utils/sound';
import { useAuth } from '../hooks/useAuth';
import { postToDiscord } from '../utils/discord';
import html2canvas from 'html2canvas';

interface ExtractedRow {
  orderId: string;
  customerName: string;
  accountNumber: string;
  ifscCode: string;
  amount: string;
  isDuplicate?: boolean;
  duplicateTimeAgo?: string;
  error?: string; // For handling success transaction errors
  formatType?: string; // 'old' or 'new'
  paymentMode?: string; // 'IMPS', 'NEFT', 'RTGS', etc.
  bankName?: string; // Added for RBL export
}

function formatCustomerName(name: string): string {
  // Replace S/O, D/O, W/O with So, Do, Wo (case-insensitive)
  let formatted = name.replace(/S\/O/gi, 'So').replace(/D\/O/gi, 'Do').replace(/W\/O/gi, 'Wo');
  // Proper-case each word
  formatted = formatted
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return formatted;
}

function cleanCustomerName(name: string): string {
  // Remove everything except alphabets, spaces, and S/O, D/O, W/O
  return name
    .split(' ')
    .map(word => {
      if (/^(S\/O|D\/O|W\/O)$/i.test(word)) return word.toUpperCase();
      return word.replace(/[^A-Za-z]/g, '');
    })
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const getTodayDateStr = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getYesterdayDateStr = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yyyy = yesterday.getFullYear();
  const mm = String(yesterday.getMonth() + 1).padStart(2, '0');
  const dd = String(yesterday.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const fetchExcelRecords = async (date: string): Promise<any[]> => {
  try {
    const data = await excelGeneratorAPI.getRecordsByDate(date);
    return data;
  } catch (error) {
    console.error('Error fetching excel records:', error);
    return [];
  }
};

const fetchAllRecords = async (): Promise<any[]> => {
  try {
    const data = await excelGeneratorAPI.getAllRecords();
    return data;
  } catch (error) {
    console.error('Error fetching all excel records:', error);
    return [];
  }
};

const fetchAllStaff = async (): Promise<any[]> => {
  try {
    let result: any;
    try {
      result = await staffAPI.getStaff();
    } catch (adminError) {
      result = await staffAPI.getStaffNames();
    }
    
    if (Array.isArray(result)) {
      return result.map(s => (typeof s === 'string' ? { name: s } : s));
    }
    return [];
  } catch (error) {
    console.error('Error fetching staff list:', error);
    return [];
  }
};

const addExcelRecord = async (row: any) => {
  try {
    await excelGeneratorAPI.addExcelRecord(row);
  } catch (error) {
    console.error('Error adding excel record:', error);
  }
};

// Global duplicate check across all users by Order ID
const checkGlobalDuplicate = async (orderId: string, date: string) => {
  try {
    const result = await excelGeneratorAPI.checkGlobalDuplicate(orderId, date);
    return result;
  } catch (error) {
    console.error('Error checking global duplicate:', error);
    return { isDuplicate: false };
  }
};

// Get global file counter
const getFileCounter = async (name: string) => {
  try {
    const result = await excelGeneratorAPI.getFileCounter(name);
    return result;
  } catch (error) {
    console.error('Error getting file counter:', error);
    return { currentCounter: 1 };
  }
};

// Increment global file counter
const incrementFileCounter = async (name: string) => {
  try {
    const result = await excelGeneratorAPI.incrementFileCounter(name);
    return result;
  } catch (error) {
    console.error('Error incrementing file counter:', error);
    // Fallback in case of error to maintain type consistency
    return { 
      fileNumber: String(Math.floor(Math.random() * 100000000)).padStart(8, '0'),
      usedCounter: 0,
      nextCounter: 0,
    };
  }
};

const ExcelGenerator: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [inputValue, setInputValue] = useState('');
  const [newInputValue, setNewInputValue] = useState('');
  const [rows, setRows] = useState<ExtractedRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dbHistory, setDbHistory] = useState<any[]>([]);
  const [allStaff, setAllStaff] = useState<any[]>([]);
  const [showDebug, setShowDebug] = useState(false);
  const [loading, setLoading] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [showICICIUploadModal, setShowICICIUploadModal] = useState(false);
  const [lastICICIFilePath, setLastICICIFilePath] = useState<string | null>(null);

  const overviewRef = useRef<HTMLDivElement>(null);

  const handleShareToDiscord = async () => {
    if (!overviewRef.current) {
      toast.error('Overview section not found.');
      return;
    }

    try {
      toast('Capturing screenshot...', { icon: '📸' });

      // Create a temporary wrapper for padding
      const wrapper = document.createElement('div');
      wrapper.style.background = '#fff';
      wrapper.style.padding = '16px';
      wrapper.style.display = 'inline-block';
      wrapper.appendChild(overviewRef.current.cloneNode(true));
      document.body.appendChild(wrapper);

      const canvas = await html2canvas(wrapper, {
        useCORS: true,
        scale: 2,
        backgroundColor: '#fff',
      });

      document.body.removeChild(wrapper);

      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error('Failed to create image blob.');
          return;
        }

        const formData = new FormData();
        const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        const fileName = `cashout-statics-${today.replace(/ /g, '-')}.png`;
        
        formData.append('file', blob, fileName);
        formData.append('content', `📊 Cashout Statics for **${today}**`);

        toast('Uploading to Discord...', { icon: '⏳' });
        const resp = await postToDiscord(formData);

        if (resp.ok) {
          toast.success('Statics shared to Discord successfully!');
        } else {
          toast.error(`Failed to share to Discord. Status: ${resp.status}`);
        }
      }, 'image/png');

    } catch (error) {
      console.error('Error sharing to Discord:', error);
      toast.error('An error occurred while sharing.');
    }
  };

  // Authentication check - prevent redirect on page reload
  useEffect(() => {
    // Add a delay to allow authentication to be restored from sessionStorage
    const timer = setTimeout(() => {
      if (!isAuthenticated) {
        navigate('/login');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [isAuthenticated, navigate]);

  // Fetch only today's records and staff data on mount
  useEffect(() => {
    const fetchHistory = async () => {
      const todayDateStr = getTodayDateStr();
      
      try {
        // Fetch only today's records and staff data
        const [todayHistory, staffList] = await Promise.all([
          fetchExcelRecords(todayDateStr),
          fetchAllStaff()
        ]);
        
        // Sort today's records by timestamp (latest first)
        const sortedRecords = todayHistory.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
        
        setDbHistory(sortedRecords);
        
        // If staff API fails, create staff list from today's records
        if (staffList.length === 0 && todayHistory.length > 0) {
          const uniqueStaff = Array.from(new Set(
            todayHistory
              .map(record => record.addedByName)
              .filter(name => name)
          )).map(name => ({ name }));
          setAllStaff(uniqueStaff);
        } else {
          setAllStaff(staffList);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback
        setDbHistory([]);
        setAllStaff([]);
      }
    };
    
    // Add delay to ensure auth is loaded
    const timer = setTimeout(fetchHistory, 200);
    return () => clearTimeout(timer);
  }, []);

  // Parse 1 record from structured text (now 19-21 lines with Order ID)
  const parseStructuredText = (text: string): ExtractedRow | null => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    
    console.log('🔍 Parsing lines:', lines.length);
    console.log('📄 Lines:', lines);
    
    // Handle 19-line format (your current data)
    if (lines.length >= 18 && lines.length <= 21) {
      const orderId = lines[0];
      
      // Find customer name - usually around line 6-7, look for name pattern
      let customerName = '';
      for (let i = 2; i < 8; i++) {
        if (lines[i] && /^[A-Za-z\s\/]+$/.test(lines[i]) && lines[i].length > 3) {
          customerName = cleanCustomerName(lines[i]);
          break;
        }
      }
      if (!customerName) customerName = cleanCustomerName(lines[6] || lines[2]);
      customerName = formatCustomerName(customerName);
      
      // Find account number - look for 10+ digit number
      let accountNumber = '';
      for (let i = 8; i < 12; i++) {
        if (lines[i] && /^[0-9]{10,}$/.test(lines[i])) {
          accountNumber = lines[i];
          break;
        }
      }
      if (!accountNumber) accountNumber = lines[8] || lines[9];
      
      // Find IFSC - look for IFSC pattern
      let ifscCode = '';
      for (let i = 9; i < 13; i++) {
        if (lines[i] && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(lines[i])) {
          ifscCode = lines[i];
          break;
        }
      }
      if (!ifscCode) ifscCode = lines[9] || lines[10];
      
      // Find amount - look for decimal number
      let amount = '';
      for (let i = 12; i < 16; i++) {
        if (lines[i] && /^[0-9]+(\.[0-9]{1,2})?$/.test(lines[i])) {
          amount = lines[i];
          break;
        }
      }
      if (!amount) amount = lines[12] || lines[13];
      
      // Check if transaction is already successful (line 11 should be null)
      const line11 = lines[10] || ''; // Line 11 (0-indexed as 10)
      if (line11.toLowerCase() !== 'null' && line11.trim() !== '') {
        console.log('❌ Transaction already successful, line 11:', line11);
        return null; // Return null and handle error in main function
      }
      
      console.log('🎯 Extracted:', { orderId, customerName, accountNumber, ifscCode, amount });
      
      if (!orderId || !customerName || !accountNumber || !ifscCode || !amount) {
        console.log('❌ Missing required fields');
        return null;
      }
      if (customerName.length < 2 || customerName.length > 50) {
        console.log('❌ Invalid customer name length');
        return null;
      }
      
      let paymentMode = 'IMPS'; // Default
      const paymentModeLine = lines.find(l => l.toLowerCase().includes('cash out :'));
      if (paymentModeLine) {
        const mode = paymentModeLine.split(':')[1]?.trim().toUpperCase();
        if (mode && ['IMPS', 'NEFT', 'RTGS'].includes(mode)) {
          paymentMode = mode;
        }
      }

      const bankName = lines.find(line => line.toLowerCase().includes('bank')) || 'Unknown Bank';
      
      return { orderId, customerName, accountNumber, ifscCode, amount, paymentMode, bankName };
    }
    
    // Handle 8-line format (old format)
    if (lines.length === 8) {
      if (lines[4].toLowerCase() !== 'null' || lines[7].toLowerCase() !== 'null') return null;
      let customerName = cleanCustomerName(lines[0]);
      customerName = formatCustomerName(customerName);
      if (!customerName) return null;
      if (customerName.length < 2 || customerName.length > 50) return null;
      const accountNumber = lines[2];
      const ifscCode = lines[3];
      const amount = lines[6];
      if (!customerName || !accountNumber || !ifscCode || !amount) return null;
      const orderId = `OLD${Date.now()}${Math.random().toString(36).substr(2, 5)}`.toUpperCase();
      return { orderId, customerName, accountNumber, ifscCode, amount, bankName: 'Unknown Bank' };
    }
    
    console.log('❌ Unsupported format - lines:', lines.length);
    return null;
  };

  // Helper to robustly split a line by tab or multiple spaces
  const splitLine = (line: string) => line.split(/\t| {2,}/).map(s => s.trim()).filter(Boolean);

  // New Format parsing (updated with correct line positions after normalization)
  const parseNewFormat = (text: string): ExtractedRow | null => {
    if (!text.trim()) return null;
    
    console.log('🔍 New Format Raw Text (first 300 chars):', text.substring(0, 300));
    
    // First split by lines, then process each line separately
    const rawLines = text.split(/[\n\r]+/).map(l => l.trim()).filter(Boolean);
    console.log('🔍 Raw Lines Count:', rawLines.length);
    
    // Now extract each field using line-by-line approach with patterns
    const lines: string[] = [];
    
    // Process each raw line and extract individual elements
    rawLines.forEach(line => {
      if (line.includes('\t')) {
        // Tab separated - split by tabs
        lines.push(...line.split('\t').map(s => s.trim()).filter(Boolean));
      } else if (line.includes('  ')) {
        // Multiple spaces - split by 2+ spaces  
        lines.push(...line.split(/ {2,}/).map(s => s.trim()).filter(Boolean));
      } else {
        // Single element line
        lines.push(line);
      }
    });
    
    console.log('🔍 New Format Normalized Lines:', lines.length);
    console.log('📄 First 20 Lines:', lines.slice(0, 20).map((line, i) => `${i+1}: ${line}`));
    
    if (lines.length < 10) return null;
    
    // Dynamic pattern-based extraction instead of fixed positions
    
    // 1. Extract Order ID - first 14-digit number
    const orderId = lines.find(line => /^[0-9]{14}$/.test(line)) || '';
    if (!orderId) {
      console.log('❌ No valid 14-digit Order ID found');
      return null;
    }
    console.log('✅ Found Order ID:', orderId);
    
    // 2. Extract Account Number - 10-18 digit number (more selective)
    const accountNumbers = lines.filter(line => /^[0-9]{10,18}$/.test(line));
    
    // Filter out Order IDs and pick most likely account number
    const validAccountNumbers = accountNumbers.filter(acc => {
      // Skip if it's the same as order ID
      if (acc === orderId) return false;
      
      // Skip if it's exactly 14 digits (likely another Order ID)
      if (acc.length === 14) return false;
      
      // Prefer 12-16 digit account numbers
      return acc.length >= 10 && acc.length <= 18;
    });
    
    // Pick the first valid account number
    const accountNumber = validAccountNumbers[0] || '';
    console.log('✅ Found Account Number:', accountNumber, '(from all numbers:', accountNumbers, ', valid:', validAccountNumbers, ')');
    
    // 3. Extract IFSC Code - pattern ABCD0123456
    const ifscCode = lines.find(line => {
      const cleaned = line.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(cleaned);
    })?.replace(/[^A-Za-z0-9]/g, '').toUpperCase() || '';
    console.log('✅ Found IFSC Code:', ifscCode);
    
    // 4. Extract Customer Name - alphabetic name (filter out status messages)
    const namePattern = /^[A-Za-z\s]{2,50}$/;
    const excludeWords = [
      'BANK', 'LIMITED', 'Transaction', 'IMPS', 'Transfer', 'CashOut', 
      'Agent', 'Business', 'Partner', 'Off-Line', 'Accepted', 'Refunded', 
      'Reversed', 'AXIS', 'ICICI', 'UNION', 'UTIB', 'ICIC', 'UBIN'
    ];
    
    const potentialNames = lines.filter(line => {
      if (!namePattern.test(line) || line.length < 2) return false;
      
      // Check if line contains any exclude words
      const hasExcludeWord = excludeWords.some(word => 
        line.toUpperCase().includes(word.toUpperCase())
      );
      
      // Skip dates, times, and numeric patterns
      if (/\d{2}\/\d{2}\/\d{2}/.test(line)) return false; // dates
      if (/\d{1,2}:\d{2}:\d{2}/.test(line)) return false; // times
      if (/\d{4,}/.test(line)) return false; // long numbers
      
      return !hasExcludeWord;
    });
    
    let customerName = potentialNames.reduce((longest, current) => 
      current.length > longest.length ? current : longest, ''
    );
    customerName = cleanCustomerName(customerName);
    customerName = formatCustomerName(customerName);
    console.log('✅ Found Customer Name:', customerName, '(from options:', potentialNames, ')');
    
    // 5. Extract Amount - numeric value (more selective filtering)
    const amounts = lines.filter(line => /^[0-9]+(\.[0-9]{1,2})?$/.test(line));
    
    // Filter out obvious non-amounts
    const validAmounts = amounts.filter(amt => {
      const num = parseFloat(amt);
      const len = amt.length;
      
      // Skip Order IDs (14 digits), Account Numbers (10+ digits), Reference IDs
      if (len >= 10) return false;
      
      // Skip very small amounts (likely fees) and unrealistic amounts
      if (num < 10 || num > 1000000) return false;
      
      // Skip if it matches Order ID or Account Number patterns  
      if (amt === orderId || amt === accountNumber) return false;
      
      return true;
    });
    
    // Pick the largest valid amount (main transaction amount)
    const amount = validAmounts.reduce((max, current) => 
      parseFloat(current) > parseFloat(max || '0') ? current : max, ''
    );
    console.log('✅ Found Amount:', amount, '(from all amounts:', amounts, ', valid:', validAmounts, ')');
    
    // 6. Check Transaction Status - Only allow "accepted" or "pending"
    const lastLine = lines[lines.length - 1]?.toLowerCase() || '';
    console.log('🔍 Transaction Status Check - Last line:', lastLine);
    
    const rejectedStatuses = ['success', 'failed', 'reversed', 'refunded'];
    const allowedStatuses = ['accepted', 'pending'];
    
    // Check if contains rejected status
    const hasRejectedStatus = rejectedStatuses.some(status => lastLine.includes(status));
    const hasAllowedStatus = allowedStatuses.some(status => lastLine.includes(status));
    
    if (hasRejectedStatus) {
      console.log('❌ Transaction status rejected:', lastLine);
      return null; // This will trigger status error handling in main function
    }
    
    if (!hasAllowedStatus) {
      console.log('⚠️ Transaction status unclear, checking content...');
      // If no clear status, check for any status keywords in the entire text
      const fullText = rawLines.join(' ').toLowerCase();
      const hasAnyRejectedStatus = rejectedStatuses.some(status => fullText.includes(status));
      
      if (hasAnyRejectedStatus) {
        console.log('❌ Found rejected status in full text');
        return null;
      }
    }
    
    console.log('✅ Transaction status accepted for processing');
    
    console.log('🎯 New Format Extracted:', { orderId, customerName, accountNumber, ifscCode, amount });
    
    if (!orderId || !customerName || !accountNumber || !ifscCode || !amount) {
      console.log('❌ Missing required fields in new format');
      console.log('Debug - orderId:', orderId);
      console.log('Debug - customerName:', customerName);
      console.log('Debug - accountNumber:', accountNumber);
      console.log('Debug - ifscCode:', ifscCode);
      console.log('Debug - amount:', amount);
      return null;
    }
    
    if (customerName.length < 2 || customerName.length > 50) {
      console.log('❌ Invalid customer name length in new format');
      return null;
    }
    
    let paymentModeNew = 'IMPS'; // Default value
    const transferLine = lines.find(line => line.toLowerCase().includes('transfer'));
    if (transferLine) {
        const upperLine = transferLine.toUpperCase();
        if (upperLine.includes('IMPS')) {
            paymentModeNew = 'IMPS';
        } else if (upperLine.includes('NEFT')) {
            paymentModeNew = 'NEFT';
        } else if (upperLine.includes('RTGS')) {
            paymentModeNew = 'RTGS';
        }
    }

    const bankName = lines.find(line => line.toLowerCase().includes('bank')) || 'Unknown Bank';
    
    return { orderId, customerName, accountNumber, ifscCode, amount, paymentMode: paymentModeNew, bankName };
  };

  // Validation helpers
  const isValidName = (name: string) => {
    // Allow alphabets, spaces, and S/O, D/O, W/O with at least 2 chars after
    const pattern = /^([A-Za-z]+(\s|$)|(S\/O|D\/O|W\/O)\s+[A-Za-z]{2,})+$/i;
    return pattern.test(name) && name.length >= 2 && name.length <= 50;
  };
  const isValidAccountNumber = (acc: string) => /^[0-9]{10,18}$/.test(acc);
  const isValidIFSC = (ifsc: string) => {
    console.log('🔍 IFSC Validation - Input:', JSON.stringify(ifsc), 'Length:', ifsc.length);
    
    // IFSC should already be cleaned from extraction, just validate the pattern
    const result = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc);
    console.log('🔍 IFSC Validation - Result:', result);
    return result;
  };
  const isValidAmount = (amt: string) => /^[0-9]+(\.[0-9]{1,2})?$/.test(amt) && parseFloat(amt) > 0;

  const handleAddData = async (format: 'old' | 'new') => {
    console.log('🚀 handleAddData called with format:', format);
    const inputText = format === 'old' ? inputValue : newInputValue;
    console.log('Input value:', inputText);
    
    setError(null);
    
    // Split input into multiple records
    const records = splitMultipleRecords(inputText, format);
    console.log('📝 Split into records:', records.length);
    
    if (records.length === 0) {
      console.log('❌ No valid records found');
      setError('No valid data found. Please check the format and try again.');
      playErrorSound();
      if (format === 'old') setInputValue('');
      else setNewInputValue('');
      return;
    }
    
    let addedCount = 0;
    let duplicateCount = 0;
    let errorCount = 0;
    let errorMessages: string[] = [];
    let processedOrderIds: string[] = []; // Track Order IDs processed in this batch
    
    // Process each record individually
    for (let i = 0; i < records.length; i++) {
      const recordText = records[i];
      console.log(`\n🔄 Processing record ${i + 1}/${records.length}`);
      
      const parsed = format === 'old' ? parseStructuredText(recordText) : parseNewFormat(recordText);
      console.log('📝 Parsed result:', parsed);
      
      if (!parsed) {
        console.log('❌ Parsing failed for this record');
        
        // Check if it's a success transaction error
        const lines = recordText.split('\n').map(l => l.trim()).filter(Boolean);
        
        if (format === 'old') {
          // Old format: Check line 11 for null
          if (lines.length >= 18 && lines.length <= 21) {
            const line11 = lines[10] || '';
            if (line11.toLowerCase() !== 'null' && line11.trim() !== '') {
              const orderId = lines[0] || 'Unknown';
              errorCount++;
              errorMessages.push(`Record ${i + 1}: This order id ${orderId} is already success`);
              continue;
            }
          }
        } else {
          // New format: Check last line for transaction status
          if (lines.length >= 10) {
            const orderId = lines[0] || 'Unknown';
            const transactionStatus = lines[lines.length - 1] || ''; // Last line
            
            console.log(`🔍 New Format Status Check - Order: ${orderId}, Status: ${transactionStatus}`);
            
            // Check for rejected statuses
            const rejectedStatuses = ['success', 'failed', 'reversed', 'refunded'];
            const hasRejectedStatus = rejectedStatuses.some(status => 
              transactionStatus.toLowerCase().includes(status)
            );
            
            if (hasRejectedStatus) {
              console.log(`❌ Transaction rejected - Status: ${transactionStatus}`);
              errorCount++;
              
              // Provide specific error message based on status
              if (transactionStatus.toLowerCase().includes('success')) {
                errorMessages.push(`Record ${i + 1}: Order ${orderId} is already successful`);
              } else if (transactionStatus.toLowerCase().includes('failed')) {
                errorMessages.push(`Record ${i + 1}: Order ${orderId} has failed status`);
              } else if (transactionStatus.toLowerCase().includes('reversed') || transactionStatus.toLowerCase().includes('refunded')) {
                errorMessages.push(`Record ${i + 1}: Order ${orderId} is already ${transactionStatus.toLowerCase()}`);
              }
              continue;
            }
          }
        }
        
        errorCount++;
        errorMessages.push(`Record ${i + 1}: Invalid data format`);
        continue;
      }
      
      // Local duplicate check by orderId only (check both existing rows and current batch)
      const isLocalDuplicate = rows.some(r => r.orderId === parsed.orderId) || processedOrderIds.includes(parsed.orderId);
      console.log('🔍 Local duplicate check:', isLocalDuplicate);
      
      if (isLocalDuplicate) {
        toast(`Record ${i + 1}: Duplicate Txn ID! Order ID ${parsed.orderId} already exists in table.`, { icon: '⚠️', duration: 4000 });
        playWarningSound();
        duplicateCount++;
        continue; // Skip adding this record to table
      }
      
      // Add to processed list
      processedOrderIds.push(parsed.orderId);
      
      // Validation
      if (!isValidName(parsed.customerName)) {
        console.log('❌ Invalid customer name for record', i + 1);
        errorCount++;
        errorMessages.push(`Record ${i + 1}: Invalid customer name - only alphabets and spaces allowed`);
        continue;
      }
      if (!isValidAccountNumber(parsed.accountNumber)) {
        console.log('❌ Invalid account number for record', i + 1);
        errorCount++;
        errorMessages.push(`Record ${i + 1}: Invalid account number - must be 10-18 digits`);
        continue;
      }
      if (!isValidIFSC(parsed.ifscCode)) {
        console.log('❌ Invalid IFSC for record', i + 1);
        errorCount++;
        errorMessages.push(`Record ${i + 1}: Invalid IFSC code format`);
        continue;
      }
      if (!isValidAmount(parsed.amount)) {
        console.log('❌ Invalid amount for record', i + 1);
        errorCount++;
        errorMessages.push(`Record ${i + 1}: Invalid amount format`);
        continue;
      }
      
      // Global duplicate check by orderId only
      const dateStr = getTodayDateStr();
      console.log('🌐 Making API call for duplicate check with:', { orderId: parsed.orderId, date: dateStr });
      
      const duplicateCheck = await checkGlobalDuplicate(parsed.orderId, dateStr);
      console.log('📡 API Response:', duplicateCheck);
      
      let rowToAdd: ExtractedRow = { ...parsed };
      rowToAdd.formatType = format;

      if (duplicateCheck.isDuplicate) {
        console.log('⚠️ Duplicate found! Setting warning...');
        rowToAdd.isDuplicate = true;
        const bankType = duplicateCheck.fileType ? `(${duplicateCheck.fileType.toUpperCase()})` : '';
        const warningMsg = (
          <span>
            Record {i + 1}: Same order id has already been processed before {duplicateCheck.timeAgo} by <b>{duplicateCheck.addedBy}</b> {bankType}
          </span>
        );
        console.log('Warning message:', warningMsg);
        toast(warningMsg, { icon: '⚠️', duration: 8000 });
        playWarningSound();
      } else {
        console.log('✅ No duplicate found');
      }
      
      // Add the row to the table (always, even if duplicate)
      console.log('➕ Adding row to table:', rowToAdd);
      setRows(prev => [...prev, rowToAdd]);
      addedCount++;
    }
    
    // Show summary and handle errors
    if (errorCount > 0) {
      // Display first few error messages on screen
      const displayErrors = errorMessages.slice(0, 3).join('\n');
      if (errorMessages.length > 3) {
        setError(displayErrors + `\n... and ${errorMessages.length - 3} more errors`);
      } else {
        setError(displayErrors);
      }
      playErrorSound();
    }
    
    if (records.length > 1) {
      const summaryMsg = `Processed ${records.length} records: ${addedCount} added, ${duplicateCount} local duplicates, ${errorCount} errors`;
      toast(summaryMsg, { icon: 'ℹ️', duration: 6000 });
    } else if (records.length === 1 && errorCount === 1) {
      // Single record with error - don't show summary toast as error is already shown on screen
      console.log('Single record failed - error shown on screen');
    }
    
    // Refresh today's records for display and clear input
    try {
      const [todayHistory, staffList] = await Promise.all([
        fetchExcelRecords(getTodayDateStr()),
        fetchAllStaff()
      ]);
      
      // Sort today's records by timestamp (latest first)
      const sortedRecords = todayHistory.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
      
      setDbHistory(sortedRecords);
      
      // Update staff list if needed
      if (staffList.length === 0 && todayHistory.length > 0) {
        const uniqueStaff = Array.from(new Set(
          todayHistory
            .map(record => record.addedByName)
            .filter(name => name)
        )).map(name => ({ name }));
        setAllStaff(uniqueStaff);
      } else {
        setAllStaff(staffList);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
    
    if (format === 'old') setInputValue('');
    else setNewInputValue('');
  };

  // Helper function to split multiple records
  const splitMultipleRecords = (text: string, format: 'old' | 'new'): string[] => {
    if (!text.trim()) return [];
    
    console.log('🔍 Splitting records for format:', format);
    
    if (format === 'old') {
      // Use basic line splitting for old format
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      const records: string[] = [];
      
      console.log('🔍 Total lines:', lines.length);
      
      // For old format, look for Order ID patterns to split records
      const orderIdPattern = /^[0-9A-Z]{12,}$/; // Order IDs are typically 12+ alphanumeric chars
      
      let recordStart = 0;
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        
        // Check if this line looks like an Order ID and we have enough lines for a complete record
        if (orderIdPattern.test(line) && (i - recordStart) >= 18) {
          // Found start of new record, save the previous one
          const recordLines = lines.slice(recordStart, i);
          console.log(`📋 Found record: lines ${recordStart} to ${i-1} (${recordLines.length} lines)`);
          records.push(recordLines.join('\n'));
          recordStart = i;
        }
      }
      
      // Add the last record
      const lastRecord = lines.slice(recordStart);
      if (lastRecord.length >= 18) {
        console.log(`📋 Last record: lines ${recordStart} to end (${lastRecord.length} lines)`);
        records.push(lastRecord.join('\n'));
      }
      
      // If no multiple records detected, try as single record
      if (records.length === 0) {
        console.log('📋 No multiple records detected, treating as single record');
        records.push(text);
      }
      
      return records;
    } else {
      // For new format - use simple approach: Split by "accepted", "reversed", "refunded" patterns
      console.log('🔍 New format raw text length:', text.length);
      
             // Split by transaction status lines that mark end of records
       const statusPatterns = ['accepted', 'reversed', 'refunded', 'success', 'failed', 'pending'];
       const lines = text.split('\n');
      
      let records: string[] = [];
      let currentRecord: string[] = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        currentRecord.push(line);
        
        // Check if this line contains a status that marks end of record
        const hasStatus = statusPatterns.some(status => 
          line.toLowerCase().includes(status)
        );
        
        if (hasStatus) {
          // End of current record
          if (currentRecord.length >= 10) {
            console.log(`📋 Found new format record by status: ${currentRecord.length} lines, ending with: ${line}`);
            records.push(currentRecord.join('\n'));
          }
          currentRecord = [];
        }
      }
      
      // Add any remaining record
      if (currentRecord.length >= 10) {
        console.log(`📋 Last new format record: ${currentRecord.length} lines`);
        records.push(currentRecord.join('\n'));
      }
      
      // If no records found, treat as single record
      if (records.length === 0) {
        console.log('📋 No multiple records detected in new format, treating as single record');
        records.push(text);
      }
      
      console.log('🎯 Final split result:', records.length, 'records');
      records.forEach((record, idx) => {
        const recordLines = record.split('\n').filter(Boolean);
        console.log(`Record ${idx + 1}: ${recordLines.length} lines`);
        console.log(`🔍 Record ${idx + 1} first 5 lines:`, recordLines.slice(0, 5));
      });
      
      return records;
    }
  };

  const handleDeleteRow = (idx: number) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const handleClearAll = () => {
    setRows([]);
    setInputValue('');
    setNewInputValue('');
    setError(null);
  };

  // Debug function to check file counter status
  const checkFileCounterStatus = async () => {
    try {
      const result: any = await excelGeneratorAPI.getDebugFileCounters();
      console.log('📊 File Counter Status:', result);
      
      const counterInfo = result.counters.find((c: any) => c.name === 'icici');
      if (counterInfo) {
        toast.success(`Current ICICI Counter: ${counterInfo.counter} (Next file: ICICI${String(counterInfo.counter).padStart(8, '0')})`);
        playSuccessSound();
      } else {
        toast('No ICICI counter found yet. Will start from ICICI00000001', { icon: 'ℹ️' });
        playWarningSound();
      }
    } catch (error) {
      console.error('Error checking file counter:', error);
      toast.error('Failed to check file counter status');
      playErrorSound();
    }
  };

  const handleDownloadExcel = () => {
    if (rows.length === 0) {
      toast.error('No data to export');
      playErrorSound();
      return;
    }
    setLoading(true);
    const ws = XLSX.utils.json_to_sheet(rows.map(r => ({
      'Customer Name': r.customerName,
      'Account Number': r.accountNumber,
      'IFSC Code': r.ifscCode,
      'Amount': r.amount
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, 'extracted_data.xlsx');
    setLoading(false);
  };

  // RBL Excel Export
  const handleRBLExport = async () => {
    if (rows.length === 0) {
      toast.error('No data to export');
      playErrorSound();
      return;
    }
    setLoading(true);

    try {
      // 1. Get a unique file number for RBL
      const counterResult = await incrementFileCounter('rbl');
      const fileNumber = counterResult.fileNumber;
      const fileName = `RBL${fileNumber}.xlsx`;
      
      // 2. Define Headers based on your new structure
      const headers = [
        'Payment Type', 'Cust Ref Number', 'Source Account Number', 'Source Narration', 
        'Destination Account Number', 'Currency', 'Amount', 'Destination Narration', 
        'Destination bank', 'Destination Bank IFS Code', 'Beneficiary Name', 
        'Beneficiary Account Type', 'Email'
      ];

      // 3. Map rows to the RBL format
      const data = rows.map(r => {
        // Column A: Payment Mode Logic
        const isRatnBank = r.ifscCode.toUpperCase().startsWith('RATN');
        let paymentMode;
        if (isRatnBank) {
          paymentMode = 'TPT';
        } else {
          const rawMode = (r.paymentMode || 'IMPS').toUpperCase();
          if (rawMode === 'IMPS' || rawMode === 'NEFT') {
            paymentMode = 'NFT';
          } else { // RTGS
            paymentMode = 'RTS';
          }
        }

        return [
          paymentMode,                      // Payment Type
          r.orderId,                        // Cust Ref Number
          '409002305623',                   // Source Account Number (Corrected)
          'Cashout',                        // Source Narration
          r.accountNumber,                  // Destination Account Number
          'INR',                            // Currency
          r.amount,                         // Amount
          'Cashout',                        // Destination Narration
          r.bankName,                       // Destination bank
          r.ifscCode,                       // Destination Bank IFS Code
          r.customerName,                   // Beneficiary Name
          'Saving',                         // Beneficiary Account Type
          ''                                // Email (Blank)
        ];
      });

      // 4. Create Excel sheet
      const wsData = [headers, ...data];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // 5. Format columns C, E, G as Text
      const textCols = [2, 4, 6]; // 0-indexed columns
      // Start from row 1 to skip headers
      for (let i = 1; i <= data.length; i++) {
        textCols.forEach(colIndex => {
          const cellRef = XLSX.utils.encode_cell({ r: i, c: colIndex });
          if (ws[cellRef]) {
            ws[cellRef].t = 's'; // Set cell type to string
            ws[cellRef].z = '@'; // Set format to Text
          }
        });
      }
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, fileName);

      // After XLSX.writeFile(wb, fileName);
      // But before saving to DB
      const recordCount = rows.length;
      const totalAmount = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

      // Send to Discord webhook
      try {
        const formData = new FormData();
        formData.append('file', new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]), fileName);
        
        const userName = user?.name || user?.mobileNumber || 'Unknown User';
        const contentMsg = `📁 RBL File prepared by **${userName}**,  Records: **${recordCount}**,  Amount: **₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}**`;
        formData.append('content', contentMsg);
        
        const resp = await postToDiscord(formData);
        
        if (!resp.ok) {
          toast.error(`Note: Failed to send RBL file to Discord webhook. Status: ${resp.status}`);
        }
      } catch (error: any) {
        toast.error(`Note: Error sending RBL file to Discord webhook: ${error.message}`);
      }
      
      // 6. Save records to DB
      const timestamp = Date.now();
      const dateStr = getTodayDateStr();
      rows.forEach(async (r) => {
        await addExcelRecord({ 
          ...r, 
          timestamp, 
          date: dateStr,
          fileNumber: fileName,
          downloadedAt: new Date(),
          fileType: 'RBL', // Set file type to RBL
          formatType: r.formatType // Use format type from the row
        });
      });

      toast.success(`RBL file downloaded successfully! Records will be cleared.`);
      playSuccessSound();

      // Clear table after a small delay to allow DB saving
      setTimeout(async () => {
        setRows([]);
        setInputValue('');
        setNewInputValue('');
        setError(null);
        // Refresh history to show new entries
        try {
          const [todayHistory, staffList] = await Promise.all([
            fetchExcelRecords(getTodayDateStr()),
            fetchAllStaff()
          ]);
          const sortedRecords = todayHistory.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
          setDbHistory(sortedRecords);
          setAllStaff(staffList.length > 0 ? staffList : allStaff);
        } catch (error) {
          console.error('Error refreshing data after RBL export:', error);
        }
      }, 1500);

    } catch (error) {
      console.error('RBL Export error:', error);
      toast.error('Failed to export RBL file');
      playErrorSound();
    } finally {
      setLoading(false);
    }
  };

  // ICICI Excel Export
  const handleICICIExport = async () => {
    if (rows.length === 0) {
      toast.error('No data to export');
      playErrorSound();
      return;
    }
    setLoading(true);
    try {
      // Get global file counter (shared across all users)
      const counterResult = await incrementFileCounter('icici');
      const fileNumber = counterResult.fileNumber;
      
      console.log(`ICICI Export - Global File Counter: ${fileNumber} (used: ${counterResult.usedCounter}, next: ${counterResult.nextCounter})`);
      
      // Define headers as per sample (19 columns)
      const headers = [
        'PYMT_PROD_TYPE_CODE', 'PYMT_MODE', 'DEBIT_ACC_NO', 'BNF_NAME', 'BENE_ACC_NO', 'BENE_IFSC', 'AMOUNT',
        'DEBIT_NARR', 'CREDIT_NARR', 'MOBILE_NUM', 'EMAIL_ID', 'REMARK', 'PYMT_DATE', 'REF_NO',
        'ADDL_INFO1', 'ADDL_INFO2', 'ADDL_INFO3', 'ADDL_INFO4', 'ADDL_INFO5'
      ];
      const today = format(new Date(), 'dd-MM-yyyy');
      const data = rows.map(r => {
        const isICICI = r.ifscCode.toUpperCase().startsWith('ICIC');
        const paymentMode = isICICI ? 'FT' : (r.paymentMode || 'IMPS').toUpperCase();

        const arr = [
          'PAB_VENDOR',
          paymentMode,
          '097805004787',
          r.customerName,
          r.accountNumber,
          r.ifscCode,
          r.amount,
          'Cashout',
          'Cashout',
          '', '', '',
          today,
          '', '', '', '', '', ''
        ];
        arr[13] = r.orderId; // Column N
        return arr;
      });
      // Add header row
      const wsData = [headers, ...data];
      // Set Account Number columns as text (E and C)
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      // Force column C (2) and E (4) to be text
      const colText = [2, 4];
      for (let rowIdx = 1; rowIdx <= rows.length; rowIdx++) {
        colText.forEach(colIdx => {
          const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
          if (ws[cellRef]) ws[cellRef].z = '@';
        });
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const fileName = `ICICI${fileNumber}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      // Save download history to DB (all rows with file information)
      const timestamp = Date.now();
      const dateStr = getTodayDateStr();
      
      rows.forEach(async (r) => {
        await addExcelRecord({ 
          ...r, 
          timestamp, 
          date: dateStr,
          fileNumber: fileName,
          downloadedAt: new Date(),
          fileType: 'ICICI',
          formatType: r.formatType // Use the formatType from the row object itself
        });
      });
      
      // After a short delay, refresh today's records for accurate duplicate check
      setTimeout(async () => {
        try {
          const [todayHistory, staffList] = await Promise.all([
            fetchExcelRecords(getTodayDateStr()),
            fetchAllStaff()
          ]);
          
          // Sort today's records by timestamp (latest first)
          const sortedRecords = todayHistory.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
          
          setDbHistory(sortedRecords);
          
          // Update staff list if needed
          if (staffList.length === 0 && todayHistory.length > 0) {
            const uniqueStaff = Array.from(new Set(
              todayHistory
                .map(record => record.addedByName)
                .filter(name => name)
            )).map(name => ({ name }));
            setAllStaff(uniqueStaff);
          } else {
            setAllStaff(staffList);
          }
        } catch (error) {
          console.error('Error refreshing data after export:', error);
        }
      }, 1500);

      // Store values before clearing for Discord webhook
      const recordCount = rows.length;
      const totalAmount = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

      // Clear all records and inputs after successful ICICI file generation and database save
      setRows([]);
      setInputValue('');
      setNewInputValue('');
      setError(null);

      // Send to Discord webhook
      try {
        const formData = new FormData();
        formData.append('file', new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]), fileName);
        
        const userName = user?.name || user?.mobileNumber || 'Unknown User';
        const contentMsg = `📁 ICICI File prepared by **${userName}**,  Records: **${recordCount}**,  Amount: **₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}**`;
        formData.append('content', contentMsg);
        
        console.log(`Excel Generator Discord Webhook:`, {
          hostname: window.location.hostname,
          fileName: fileName,
          records: recordCount,
          totalAmount: totalAmount
        });
        
        const resp = await postToDiscord(formData);
        
        if (resp.ok) {
          const responseText = await resp.text();
          console.log('Discord webhook response:', responseText);
          toast.success(`ICICI file downloaded successfully! Records cleared.`);
          playSuccessSound();
        } else {
          const errorText = await resp.text();
          console.error('Discord webhook error:', resp.status, errorText);
          toast.success(`ICICI file downloaded successfully! Records cleared.`);
          toast.error(`Note: Failed to send file to Discord webhook. Status: ${resp.status}`);
          playSuccessSound();
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
        toast.success(`ICICI file downloaded successfully! Records cleared.`);
        toast.error(`Note: Error sending file to Discord webhook: ${errorMessage}`);
        playSuccessSound();
      }
      setLastICICIFilePath(fileName);
      setShowICICIUploadModal(true);
      // Do not clear rows/inputs yet, wait for user action
    } catch (error) {
      console.error('ICICI Export error:', error);
      toast.error('Failed to export ICICI file');
      playErrorSound();
    }
    setLoading(false);
  };

  // Calculate total records and total amount
  const totalRecords = rows.length;
  const totalAmount = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  // Check for duplicate order IDs in today's records
  const getDuplicateOrderIds = () => {
    const orderIdCounts: Record<string, number> = {};
    const duplicateIds = new Map<string, number>(); // Map to store orderId and its count
    
    dbHistory.forEach(record => {
      const orderId = record.orderId;
      if (orderId) {
        orderIdCounts[orderId] = (orderIdCounts[orderId] || 0) + 1;
        if (orderIdCounts[orderId] > 1) {
          duplicateIds.set(orderId, orderIdCounts[orderId]);
        }
      }
    });
    
    return duplicateIds;
  };

  // Calculate today's dashboard stats
  const todayRecords = dbHistory.length;
  const todayTotalAmount = dbHistory.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  
  // Calculate duplicate records and amount for today (excluding first occurrence)
  const duplicateOrderIds = getDuplicateOrderIds();
  
  // Count only actual duplicate records (excluding first occurrence of each order ID)
  let duplicateRecordsCount = 0;
  let duplicateTotalAmount = 0;
  const seenOrderIds = new Set<string>();
  
  dbHistory.forEach(record => {
    if (duplicateOrderIds.has(record.orderId)) {
      if (seenOrderIds.has(record.orderId)) {
        // This is a duplicate (not the first occurrence)
        duplicateRecordsCount++;
        duplicateTotalAmount += parseFloat(record.amount) || 0;
      } else {
        // This is the first occurrence, mark as seen but don't count as duplicate
        seenOrderIds.add(record.orderId);
      }
    }
  });

  // Get staff counts for today and yesterday (from all staff list)
  const getTodayStaffCounts = () => {
    const counts: Record<string, number> = {};
    
    // Initialize all staff with 0
    allStaff.forEach(staff => {
      counts[staff.name] = 0;
    });
    
    // Count actual records
    dbHistory.forEach(record => {
      const staffName = record.addedByName;
      if (staffName && counts.hasOwnProperty(staffName)) {
        counts[staffName] = (counts[staffName] || 0) + 1;
      }
    });
    return counts;
  };

  const todayStaffCounts = getTodayStaffCounts();

  // Get all staff names from system
  const allStaffNames = allStaff.map(staff => staff.name).sort();



  // Filter records based on search query
  const filteredRecords = dbHistory.filter(record => {
    const searchTerm = searchQuery.toLowerCase();
    return (
      record.orderId?.toLowerCase().includes(searchTerm) ||
      record.customerName?.toLowerCase().includes(searchTerm) ||
      record.amount?.toString().toLowerCase().includes(searchTerm) ||
      record.accountNumber?.toLowerCase().includes(searchTerm) ||
      record.ifscCode?.toLowerCase().includes(searchTerm) ||
      record.fileNumber?.toLowerCase().includes(searchTerm) ||
      record.addedByName?.toLowerCase().includes(searchTerm)
    );
  });

  // Pagination logic
  const totalRecordsCount = filteredRecords.length;
  const totalPages = Math.ceil(totalRecordsCount / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  // 1. Fix Total Download Files counter
  const uniqueFileNumbers = Array.from(new Set(dbHistory.filter(record => record.fileNumber).map(record => record.fileNumber)));
  const todayDownloadFilesCount = uniqueFileNumbers.length;

  // Calculate new stats for the overview cards
  const oldFormatRecordsCount = dbHistory.filter(r => r.formatType === 'old').length;
  const newFormatRecordsCount = dbHistory.filter(r => r.formatType === 'new').length;
  const iciciRecordsCount = dbHistory.filter(r => r.fileType === 'ICICI').length;
  const rblRecordsCount = dbHistory.filter(r => r.fileType === 'RBL').length;

  const oldFormatTotalAmount = dbHistory.filter(r => r.formatType === 'old').reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const newFormatTotalAmount = dbHistory.filter(r => r.formatType === 'new').reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const iciciTotalAmount = dbHistory.filter(r => r.fileType === 'ICICI').reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const rblTotalAmount = dbHistory.filter(r => r.fileType === 'RBL').reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  
  // Replace the old grid with the new two-row, six-column grid structure
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 mb-4">Today's Overview</h2>
    {/* Row 1: Record Counts */}
    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
      {/* Total Records */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">Total Records</p>
        <p className="text-2xl font-semibold text-blue-900">{todayRecords}</p>
      </div>
      {/* New Format Records */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">New Format</p>
        <p className="text-2xl font-semibold text-gray-800">{newFormatRecordsCount}</p>
      </div>
      {/* Old Format Records */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">Old Format</p>
        <p className="text-2xl font-semibold text-gray-800">{oldFormatRecordsCount}</p>
      </div>
      {/* Duplicate Records */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">Duplicate</p>
        <p className="text-2xl font-semibold text-purple-900">{duplicateRecordsCount}</p>
      </div>
      {/* For ICICI */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">For ICICI</p>
        <p className="text-2xl font-semibold text-green-800">{iciciRecordsCount}</p>
      </div>
      {/* For RBL */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">For RBL</p>
        <p className="text-2xl font-semibold text-purple-800">{rblRecordsCount}</p>
      </div>
    </div>
    {/* Row 2: Amount Counts */}
    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
      {/* Total Amount */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">Total Amount</p>
        <p className="text-2xl font-semibold text-green-900">₹{todayTotalAmount.toLocaleString('en-IN')}</p>
      </div>
      {/* New Format Amount */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">New Format</p>
        <p className="text-2xl font-semibold text-gray-800">₹{newFormatTotalAmount.toLocaleString('en-IN')}</p>
      </div>
      {/* Old Format Amount */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">Old Format</p>
        <p className="text-2xl font-semibold text-gray-800">₹{oldFormatTotalAmount.toLocaleString('en-IN')}</p>
      </div>
      {/* Duplicate Amount */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">Duplicate</p>
        <p className="text-2xl font-semibold text-orange-900">₹{duplicateTotalAmount.toLocaleString('en-IN')}</p>
      </div>
      {/* For ICICI Amount */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">For ICICI</p>
        <p className="text-2xl font-semibold text-green-800">₹{iciciTotalAmount.toLocaleString('en-IN')}</p>
      </div>
      {/* For RBL Amount */}
      <div className="bg-white rounded-lg shadow p-4 text-center">
        <p className="text-sm font-medium text-gray-500">For RBL</p>
        <p className="text-2xl font-semibold text-purple-800">₹{rblTotalAmount.toLocaleString('en-IN')}</p>
      </div>
    </div>
    {/* ... rest of the component */}
  </div>

  // Enhanced automation trigger - supports both local backend and local agent
    const handleICICIAutoUpload = async (filePath: string) => {
    // 1. Check if the local agent is running by pinging it
    try {
      const response = await fetch('http://localhost:8081/ping');
      if (!response.ok) {
        throw new Error('Agent not responding');
      }
    } catch (error) {
      toast.error('Automation Agent is not running. Please start it and try again.');
      playErrorSound();
      return;
    }

    setShowICICIUploadModal(false);

    try {
      // First, try the backend automation
      console.log('🚀 Attempting backend automation...');
      const backendResponse = await fetch('/api/icici-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: filePath }),
      });

      if (backendResponse.ok) {
        const result = await backendResponse.json();
        // Check if the backend *actually* started the automation
        if (result.automationStarted) {
          toast.success('✅ ICICI upload automation started via backend!');
          console.log('✅ Backend automation successful');
          return; // Success, no need to call local agent
        } else {
          // Backend is OK, but didn't start automation (e.g., on live server)
          console.log('⚠️ Backend cannot run automation, trying local agent...');
        }
      } else {
        // Backend returned an error (e.g., 500)
        console.log('⚠️ Backend automation failed, trying local agent...');
      }
    } catch (backendError) {
      console.log('⚠️ Backend automation error, trying local agent...', backendError);
    }

    // If backend automation didn't run, try the local automation agent
    try {
      console.log('🤖 Attempting local agent automation...');
      const agentResponse = await fetch('http://localhost:8081/trigger-automation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: filePath }),
      });

      if (agentResponse.ok) {
        const result = await agentResponse.json();
        toast.success('✅ ICICI upload automation started via local agent!');
        console.log('✅ Local agent automation successful:', result);
        return;
      } else {
        const errorResult = await agentResponse.json();
        console.error('❌ Local agent error:', errorResult);
        toast.error(`Local agent error: ${errorResult.error || 'Unknown error'}`);
      }
    } catch (agentError) {
      console.error('❌ Local agent connection failed:', agentError);
      toast.error('❌ Could not connect to local automation agent. Make sure it is running.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-md border-b z-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img src="/logo.png" alt="Logo" className="h-8 w-auto" />
            </div>
            
            {/* Title */}
            <div className="flex-1 text-center">
              <h1 className="text-2xl font-bold text-gray-900">Excel Generator</h1>
            </div>
            
            {/* Back Button */}
            <div className="flex-shrink-0">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                <span>Back to Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content with top padding for fixed header */}
      <div className="pt-24"> {/* Added padding top */}
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Dashboard Overview */}
          <div className="mb-8" ref={overviewRef}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Today's Overview</h2>
              <button
                onClick={handleShareToDiscord}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm"
              >
                <span>Share</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" /></svg>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {/* Total */}
              <div className="bg-white rounded-lg shadow p-4 flex flex-col justify-center items-start">
                <p className="text-sm font-medium text-gray-500 mb-1 text-left">Total</p>
                <span className="text-2xl font-bold text-green-900 mb-1 text-left">₹{todayTotalAmount.toLocaleString('en-IN')}</span>
                <span className="text-lg font-semibold text-blue-900 text-left">{todayRecords} Records</span>
              </div>
              {/* New Format */}
              <div className="bg-white rounded-lg shadow p-4 flex flex-col justify-center items-start">
                <p className="text-sm font-medium text-gray-500 mb-1 text-left">New Format</p>
                <span className="text-2xl font-bold text-gray-800 mb-1 text-left">₹{newFormatTotalAmount.toLocaleString('en-IN')}</span>
                <span className="text-lg font-semibold text-gray-800 text-left">{newFormatRecordsCount} Records</span>
              </div>
              {/* Old Format */}
              <div className="bg-white rounded-lg shadow p-4 flex flex-col justify-center items-start">
                <p className="text-sm font-medium text-gray-500 mb-1 text-left">Old Format</p>
                <span className="text-2xl font-bold text-gray-800 mb-1 text-left">₹{oldFormatTotalAmount.toLocaleString('en-IN')}</span>
                <span className="text-lg font-semibold text-gray-800 text-left">{oldFormatRecordsCount} Records</span>
              </div>
              {/* Duplicate */}
              <div className="bg-white rounded-lg shadow p-4 flex flex-col justify-center items-start">
                <p className="text-sm font-medium text-gray-500 mb-1 text-left">Duplicate</p>
                <span className="text-2xl font-bold text-orange-900 mb-1 text-left">₹{duplicateTotalAmount.toLocaleString('en-IN')}</span>
                <span className="text-lg font-semibold text-purple-900 text-left">{duplicateRecordsCount} Records</span>
              </div>
              {/* For ICICI */}
              <div className="bg-white rounded-lg shadow p-4 flex flex-col justify-center items-start">
                <p className="text-sm font-medium text-gray-500 mb-1 text-left">For ICICI</p>
                <span className="text-2xl font-bold text-green-800 mb-1 text-left">₹{iciciTotalAmount.toLocaleString('en-IN')}</span>
                <span className="text-lg font-semibold text-green-800 text-left">{iciciRecordsCount} Records</span>
              </div>
              {/* For RBL */}
              <div className="bg-white rounded-lg shadow p-4 flex flex-col justify-center items-start">
                <p className="text-sm font-medium text-gray-500 mb-1 text-left">For RBL</p>
                <span className="text-2xl font-bold text-purple-800 mb-1 text-left">₹{rblTotalAmount.toLocaleString('en-IN')}</span>
                <span className="text-lg font-semibold text-purple-800 text-left">{rblRecordsCount} Records</span>
              </div>
            </div>
            {/* Staff-wise Processing Stats */}
            <div className="mt-6">
              <div style={{ fontSize: '0.75rem' }}>
                <span className="font-semibold text-gray-700">Records Processed By:</span>
                <span className="ml-2">
                  {Object.keys(todayStaffCounts).length === 0 ? (
                    <span className="text-gray-400">No data</span>
                  ) : (
                    Object.entries(todayStaffCounts)
                      .filter(([name, count]) => count > 0)
                      .map(([staffName, count], idx, arr) => (
                        <span key={staffName}>
                          {count} {staffName}
                          {idx < arr.length - 1 ? ' | ' : ''}
                        </span>
                      ))
                  )}
                  {Object.entries(todayStaffCounts).filter(([name, count]) => count > 0).length === 0 && Object.keys(todayStaffCounts).length > 0 && (
                    <span className="text-gray-400">No data</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Excel Generator */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold">Excel Generator</h2>
                <button 
                  onClick={checkFileCounterStatus}
                  className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded"
                  title="Check current file counter status"
                >
                  📊 Counter
                </button>
              </div>
              
              {/* Total Download Files Counter */}
              <div className="text-right">
                <p className="text-sm font-medium text-gray-600">Total Download Files: 
                  <span className="ml-1 text-lg font-bold text-blue-900">
                    {todayDownloadFilesCount}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label className="font-semibold text-sm mb-1">Old Format</label>
                <textarea
                  className="border rounded p-2 w-full min-h-[120px] font-mono text-sm focus:outline-blue-400"
                  placeholder={`25070819214665F5BC\n08 Jul 2025 19:21:46:867\nYOGENDRA KUMAR\n8874613091\nSETTLEMENT\nCash Out : IMPS\nYOGENDRA KUMAR S/O PARIKRAMADATT\n8874613091\n751410510002135\nBKID0007514\nnull\nBANK OF INDIA\n22530.00\nnull\nOFF-LINE\nPENDING\nnull\nANDROID\nnull`}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                />
                <button
                  className="btn-primary w-full mt-2"
                  onClick={() => handleAddData('old')}
                  disabled={!inputValue.trim()}
                >
                  Add Data
                </button>
              </div>
              <div className="flex-1">
                <label className="font-semibold text-sm mb-1">New Format</label>
                <textarea
                  className="border rounded p-2 w-full min-h-[120px] font-mono text-sm focus:outline-blue-400"
                  placeholder={`Example (13+ lines after normalization):\n20250707536233\n07 Jul 25 - 08:19:27 PM\nMAHTAB ALAM (9696969696)\n(Agent )\nCashOut\nIMPS Transfer\n915010064900111\nAXIS BANK\nUTIB0002699\nMAHTAB ALAM\nOff-Line\nDCI7128086995\n100`}
                  value={newInputValue}
                  onChange={e => {
                    // Split on 2+ spaces or tab, put each value on a new line, and trim each value
                    const raw = e.target.value;
                    const lines = raw.split(/\r?\n/);
                    const normalized = lines
                      .map(line => line.split(/\t| {2,}/).map(v => v.trim()).filter(Boolean))
                      .flat()
                      .join('\n');
                    setNewInputValue(normalized);
                  }}
                />
                <button
                  className="btn-primary w-full mt-2"
                  onClick={() => handleAddData('new')}
                  disabled={!newInputValue.trim()}
                >
                  Add Data
                </button>
              </div>
            </div>
            {error && <div className="text-red-600 text-xs mt-1">{error}</div>}
            <div className="mt-4">
              <div className="flex items-center mb-2 text-sm font-semibold w-full">
                <div className="flex-1 text-left"><span className="font-semibold">Preview</span></div>
                <div className="flex-1 text-center">Total Records: <span className="text-blue-700">{totalRecords}</span></div>
                <div className="flex-1 text-right">Total Amount: <span className="text-green-700">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full border text-xs">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-2 py-1 border">Sr No</th>
                      <th className="px-2 py-1 border">Format Type</th>
                      <th className="px-2 py-1 border">Order ID</th>
                      <th className="px-2 py-1 border">Customer Name</th>
                      <th className="px-2 py-1 border">Account Number</th>
                      <th className="px-2 py-1 border">IFSC Code</th>
                      <th className="px-2 py-1 border">Amount</th>
                      <th className="px-2 py-1 border">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-4 text-gray-400">No data</td></tr>
                    ) : rows.map((row, idx) => (
                      <tr key={idx} className={row.isDuplicate ? 'bg-red-100' : 'hover:bg-blue-50'}>
                        <td className="border px-2 py-1 text-center font-semibold">{idx + 1}</td>
                        <td className="border px-2 py-1 text-center">{row.formatType === 'old' ? 'Old Format' : row.formatType === 'new' ? 'New Format' : '-'}</td>
                        <td className="border px-2 py-1 font-mono">{row.orderId}</td>
                        <td className="border px-2 py-1 font-semibold text-gray-800">{row.customerName}</td>
                        <td className="border px-2 py-1 font-mono">{row.accountNumber}</td>
                        <td className="border px-2 py-1 font-mono">{row.ifscCode}</td>
                        <td className="border px-2 py-1 text-right font-bold text-green-700">{row.amount}</td>
                        <td className="border px-2 py-1 text-center">
                          <button
                            className="text-red-500 hover:text-red-700 text-xs font-bold px-2"
                            onClick={() => handleDeleteRow(idx)}
                            title="Delete row"
                          >🗑️</button>
                          {row.isDuplicate && (
                            <div className="text-xs mt-1 text-red-700">(Duplicate)</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex gap-2 mt-6 justify-end">
              <button
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm font-semibold"
                onClick={handleClearAll}
                disabled={rows.length === 0 && !inputValue && !newInputValue}
              >
                Clear All Data
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold flex items-center gap-2 pl-2 pr-4 py-1.5"
                onClick={handleDownloadExcel}
                disabled={rows.length === 0}
              >
                <div className="bg-white rounded-sm w-6 h-6 flex items-center justify-center">
                  <img src="/excel.jpg" alt="Excel Logo" className="h-5 w-auto" />
                </div>
                <span>Download Excel</span>
              </button>
              <button
                className="bg-green-600 hover:bg-green-700 text-white rounded text-sm font-semibold flex items-center gap-2 pl-2 pr-4 py-1.5"
                onClick={handleICICIExport}
                disabled={rows.length === 0}
              >
                <div className="bg-white rounded-sm w-6 h-6 flex items-center justify-center">
                  <img src="/icici.png" alt="ICICI Logo" className="h-5 w-auto" />
                </div>
                <span>ICICI File Download</span>
              </button>
              <button
                className="bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-semibold flex items-center gap-2 pl-2 pr-4 py-1.5"
                onClick={handleRBLExport}
                disabled={rows.length === 0}
              >
                <div className="bg-white rounded-sm w-6 h-6 flex items-center justify-center">
                  <img src="/rbl.png" alt="RBL Logo" className="h-5 w-auto" />
                </div>
                <span>RBL File Download</span>
              </button>
            </div>
          </div>

          {/* Processed Records */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Today's Processed Records</h2>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search records..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border rounded px-3 py-2 text-sm w-64 focus:outline-blue-400"
                />
                <div className="text-sm text-gray-500">
                  🔍
                </div>
              </div>
            </div>
            <div className="mb-4 text-sm text-gray-600">
              Total: {dbHistory.length} records ({duplicateRecordsCount} duplicates) | Showing: {totalRecordsCount > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, totalRecordsCount)} of {totalRecordsCount}
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-sm">
                <span>Records per page:</span>
                <select 
                  value={recordsPerPage} 
                  onChange={(e) => {
                    setRecordsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border text-xs">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1 border text-center">Sr No</th>
                    <th className="px-2 py-1 border text-center">Format Type</th>
                    <th className="px-2 py-1 border text-left">Order ID</th>
                    <th className="px-2 py-1 border text-left">Customer Name</th>
                    <th className="px-2 py-1 border text-left">Account Number</th>
                    <th className="px-2 py-1 border text-left">IFSC Code</th>
                    <th className="px-2 py-1 border text-right">Amount</th>
                    <th className="px-2 py-1 border text-center">File Type</th>
                    <th className="px-2 py-1 border text-left">File Number</th>
                    <th className="px-2 py-1 border text-left">Added By</th>
                    <th className="px-2 py-1 border text-left">Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.length === 0 ? (
                    <tr><td colSpan={11} className="text-center py-4 text-gray-400">No records found</td></tr>
                  ) : paginatedRecords.map((record, idx) => {
                    const isDuplicate = duplicateOrderIds.has(record.orderId);
                    const duplicateCount = duplicateOrderIds.get(record.orderId) || 0;
                    // Infer formatType if missing
                    let formatType = record.formatType;
                    if (!formatType) {
                      if (record.fileNumber && (record.fileNumber.startsWith('ICICI') || record.fileNumber.startsWith('RBL'))) {
                        formatType = 'new';
                      } else {
                        formatType = 'old';
                      }
                    }
                    return (
                      <tr key={idx} className={isDuplicate ? "bg-red-100 hover:bg-red-150" : "hover:bg-blue-50"}>
                        <td className="border px-2 py-1 text-center font-semibold">{startIndex + idx + 1}</td>
                        <td className="border px-2 py-1 text-center">{formatType === 'old' ? 'Old Format' : formatType === 'new' ? 'New Format' : '-'}</td>
                        <td className="border px-2 py-1 font-mono">{record.orderId}{isDuplicate && <span className="text-red-600 text-xs ml-1">({duplicateCount}x)</span>}</td>
                        <td className="border px-2 py-1">{record.customerName}</td>
                        <td className="border px-2 py-1 font-mono">{record.accountNumber}</td>
                        <td className="border px-2 py-1 font-mono">{record.ifscCode}</td>
                        <td className="border px-2 py-1 text-right font-bold text-green-700">{record.amount}</td>
                        <td className="border px-2 py-1 text-center">{record.fileType ? record.fileType.toUpperCase() : (record.fileNumber && record.fileNumber.startsWith('ICICI') ? 'ICICI' : record.fileNumber && record.fileNumber.startsWith('RBL') ? 'RBL' : '-')}</td>
                        <td className="border px-2 py-1 font-mono">{record.fileNumber || '-'}</td>
                        <td className="border px-2 py-1">{record.addedByName || 'Unknown'}</td>
                        <td className="border px-2 py-1">{record.timestamp ? new Date(Number(record.timestamp)).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* ICICI Upload Modal */}
      {showICICIUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-gray-800">Ready to Upload?</h3>
            <p className="text-gray-600 mb-4">Click "Yes" to start the automated upload to ICICI Bank.</p>
            
            <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-400 text-blue-800 rounded-lg">
              <p className="font-bold">Important: Automation Helper</p>
              <p className="text-sm mt-1">For automation to work, you need our helper utility running on your computer.</p>
              <a 
                href="/local-automation-agent.exe" 
                download
                className="mt-2 inline-block bg-blue-600 text-white font-bold px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-all"
              >
                Download Helper Utility (.exe)
              </a>
              <p className="text-xs mt-2">Download the file, run it, and then click "Yes".</p>
            </div>

            <div className="flex gap-4 justify-end mt-6">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2 rounded-lg shadow-md transition-transform transform hover:scale-105"
                onClick={() => lastICICIFilePath && handleICICIAutoUpload(lastICICIFilePath)}
              >
                Yes
              </button>
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold px-6 py-2 rounded-lg transition-transform transform hover:scale-105"
                onClick={() => setShowICICIUploadModal(false)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelGenerator; 